import { useEffect, useState } from "react";
import { ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export const VERDICTS = [
  ["deliver", "Deliver", "border-emerald-500 bg-emerald-500/15 text-emerald-300", "text-emerald-400 border-emerald-800 bg-emerald-400/10"],
  ["needs_work", "Needs work", "border-amber-400 bg-amber-500/15 text-amber-200", "text-amber-300 border-amber-800 bg-amber-400/10"],
  ["reject", "Reject", "border-rose-500 bg-rose-500/15 text-rose-300", "text-rose-300 border-rose-800 bg-rose-500/10"],
];
const UNREVIEWED = "text-slate-400 border-[#3A4A68] bg-transparent";

export const VerdictBadge = ({ verdict, testId, children }) => {
  const v = VERDICTS.find(([k]) => k === verdict);
  return <span data-testid={testId} className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${v ? v[3] : UNREVIEWED}`}>{v ? v[1] : "Unreviewed"}{children}</span>;
};

export const RunReview = ({ episodeId, run, onSaved }) => {
  const saved = run.review || null;
  const [verdict, setVerdict] = useState(saved?.verdict || null);
  const [note, setNote] = useState(saved?.note || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVerdict(saved?.verdict || null); setNote(saved?.note || ""); }, [saved?.verdict, saved?.note, saved?.at]);
  const dirty = (verdict || null) !== (saved?.verdict || null) || note !== (saved?.note || "");
  const save = async (v = verdict, n = note) => {
    setBusy(true);
    try {
      await api.put(`/episodes/${episodeId}/runs/${run.id}/review`, { verdict: v, note: n });
      toast.success(v ? `Verdict saved: ${VERDICTS.find(([k]) => k === v)[1]}` : "Review cleared");
      onSaved?.();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`run-review-${run.mode}`} className="border-t border-[#1c2433] pt-3 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="micro flex items-center gap-2"><ClipboardCheck size={13} /> Editorial verdict</div>
        <div data-testid={`run-review-meta-${run.mode}`} className="text-[11px] text-slate-500">
          {saved?.at ? <>Reviewed by <span className="text-slate-300">{saved.by}</span> · {fmtDate(saved.at)}</> : "Unreviewed — listen in your NLE, then record the verdict here"}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {VERDICTS.map(([k, label, active]) => (
          <button key={k} data-testid={`verdict-${k}-${run.mode}`} disabled={busy} onClick={() => setVerdict(verdict === k ? null : k)}
            className={`px-3 h-8 rounded-sm border text-xs font-semibold transition-colors ${verdict === k ? active : "border-[#253044] text-slate-300 hover:bg-[#1E2738]"}`}>{label}</button>
        ))}
        {saved && <button data-testid={`verdict-clear-${run.mode}`} disabled={busy} onClick={() => save(null, "")} className="px-2 h-8 text-[11px] text-slate-500 hover:text-slate-200">Clear</button>}
      </div>
      <textarea data-testid={`review-note-${run.mode}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={4000}
        placeholder="Notes for the team — e.g. music bleeds into FX at 12:40, ambience intact, Foley clean…"
        className="w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-cyan-500 resize-y" />
      <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-[11px] text-amber-300/80">Unsaved changes</span>}
        <button data-testid={`review-save-${run.mode}`} disabled={busy || !dirty || (!verdict && !note.trim())} onClick={() => save()}
          className="flex items-center gap-1.5 px-4 h-8 rounded-sm bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save verdict
        </button>
      </div>
    </div>
  );
};
